import { useEffect, useState, Fragment } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ClipboardList, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '@/lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const methodColor = {
  POST: 'bg-emerald-100 text-emerald-800',
  PATCH: 'bg-amber-100 text-amber-800',
  PUT: 'bg-amber-100 text-amber-800',
  DELETE: 'bg-red-100 text-red-800',
};

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [method, setMethod] = useState('all');
  const [pathContains, setPathContains] = useState('');
  const [actorEmail, setActorEmail] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (method && method !== 'all') params.method = method;
      if (pathContains) params.path_contains = pathContains;
      if (actorEmail) params.actor_email = actorEmail;
      const res = await axios.get(`${API}/audit-logs`, { params });
      setLogs(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
          <ClipboardList className="h-8 w-8 text-[#1680e5]" />
          Audit trail
        </h1>
        <p className="text-slate-600 mt-2">
          Every state-changing action taken in the WMS, tied to the acting user account.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Latest {logs.length} entries · newest first</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div className="space-y-2">
              <Label>Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="audit-filter-method"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any</SelectItem>
                  <SelectItem value="POST">POST</SelectItem>
                  <SelectItem value="PATCH">PATCH</SelectItem>
                  <SelectItem value="DELETE">DELETE</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Path contains</Label>
              <Input
                placeholder="/api/products"
                value={pathContains}
                onChange={(e) => setPathContains(e.target.value)}
                data-testid="audit-filter-path"
              />
            </div>
            <div className="space-y-2">
              <Label>Actor email</Label>
              <Input
                placeholder="user@example.com"
                value={actorEmail}
                onChange={(e) => setActorEmail(e.target.value)}
                data-testid="audit-filter-actor"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={load} className="bg-[#1680e5] hover:bg-[#1680e5]/90" data-testid="audit-apply-filters">
                <Search className="h-4 w-4 mr-2" />Apply
              </Button>
              <Button variant="outline" onClick={() => { setMethod('all'); setPathContains(''); setActorEmail(''); setTimeout(load, 0); }}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="py-12 text-center text-slate-500">Loading…</div>
          ) : logs.length === 0 ? (
            <div className="py-12 text-center text-slate-500">No audit entries match your filters.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Actor</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Path</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>IP</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((l) => (
                    <Fragment key={l.id}>
                      <TableRow
                        onClick={() => setExpandedId(expandedId === l.id ? null : l.id)}
                        className="cursor-pointer"
                        data-testid={`audit-row-${l.id}`}
                      >
                        <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                          {new Date(l.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-sm">
                          <div className="font-medium">{l.actor_email || '—'}</div>
                          <div className="text-xs text-slate-500">{l.actor_role || '—'}</div>
                        </TableCell>
                        <TableCell>
                          <Badge className={methodColor[l.method] || 'bg-slate-100'}>{l.method}</Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{l.path}</TableCell>
                        <TableCell>
                          <Badge className={l.status_code < 400 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                            {l.status_code}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-slate-500">{l.ip || '—'}</TableCell>
                      </TableRow>
                      {expandedId === l.id && (
                        <TableRow>
                          <TableCell colSpan={6} className="bg-slate-50">
                            <pre className="text-xs overflow-x-auto whitespace-pre-wrap">
{JSON.stringify({ query: l.query, body: l.body, user_agent: l.user_agent }, null, 2)}
                            </pre>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
